import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import HistoricAttorneyInfoCard from "./HistoricAttorneyInfoCard";
import AttorneyHomeAddressFields from "./AttorneyHomeAddressFields";
import {
  getAttorneyCardTitle,
  getAttorneyHomeFieldsLabel,
  getAttorneyHomeStepLabel,
  getAttorneyOfficeAddressLabel,
} from "../../attorneyInformationConfig";

function AttorneySearchAutocomplete({
  index,
  guardian,
  guardians,
  attorneys,
  loadingAttorneys,
  attorneyUi,
  errors,
  handleAttorneySelection,
}) {
  const isProbate = attorneyUi === "probate";
  return (
    <Autocomplete
      options={attorneys}
      value={attorneys.find((a) => a.value === guardian.attorney) || null}
      getOptionLabel={(option) => option.label || ""}
      filterOptions={(options, { inputValue }) => {
        if (!inputValue || inputValue.length < 3) return [];
        const input = inputValue.toLowerCase();
        return options.filter((opt) =>
          (opt.label || "").toLowerCase().includes(input)
        );
      }}
      noOptionsText="Type at least 3 characters to search for attorneys"
      loading={loadingAttorneys}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search Attorney"
          variant="outlined"
          fullWidth
          {...(isProbate
            ? {
                required: true,
                error: !!errors[`guardians.${index}.attorney`],
                helperText: errors[`guardians.${index}.attorney`],
              }
            : {})}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingAttorneys ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      onChange={(event, newValue) =>
        handleAttorneySelection(index, newValue, guardians)
      }
      {...(isProbate ? { isClearable: true } : {})}
    />
  );
}

AttorneySearchAutocomplete.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool.isRequired,
  attorneyUi: PropTypes.string.isRequired,
  errors: PropTypes.object.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
};

function AttorneyHomeAddressToggle({
  guardian,
  index,
  guardians,
  attorneys,
  errors,
  errorKeys,
  handleAttorneyHomeAddressChange,
}) {
  return (
    <TextField
      select
      label="Use attorney's home address?"
      value={guardian.is_attorney_home || "No"}
      onChange={(e) =>
        handleAttorneyHomeAddressChange(index, e.target.value, guardians, attorneys)
      }
      fullWidth
      required
      error={!!errors[errorKeys.isAttorneyHome]}
      helperText={errors[errorKeys.isAttorneyHome]}
    >
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </TextField>
  );
}

AttorneyHomeAddressToggle.propTypes = {
  guardian: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  guardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  errorKeys: PropTypes.object.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
};

function AttorneySelectedBlock({
  attorney,
  attorneyUi,
  attorneyCardTitle,
  officeAddressLabel,
  guardian,
  index,
  guardians,
  attorneys,
  errors,
  options,
  commonHomeProps,
  handleAttorneyHomeAddressChange,
}) {
  if (!attorney) return null;

  const {
    showHomeToggle = false,
    homeToggleWrapped = true,
    homeErrorKeys = {},
    googlePlaceholder = "Search Home Address",
    streetErrorKey,
    zipErrorKey,
    showHomeFieldsLabel = true,
  } = options;

  const homeStepLabel = getAttorneyHomeStepLabel(attorneyUi);
  const homeFieldsLabel = getAttorneyHomeFieldsLabel(attorneyUi);

  return (
    <>
      <HistoricAttorneyInfoCard
        title={attorneyCardTitle}
        attorney={attorney}
        officeAddressLabel={officeAddressLabel}
      />
      {showHomeToggle && (
        <Box sx={{ mb: 3, mt: homeToggleWrapped ? 3 : undefined }}>
          {homeToggleWrapped && homeStepLabel && (
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              {homeStepLabel}
            </Typography>
          )}
          <AttorneyHomeAddressToggle
            guardian={guardian}
            index={index}
            guardians={guardians}
            attorneys={attorneys}
            errors={errors}
            errorKeys={homeErrorKeys}
            handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
          />
        </Box>
      )}
      {guardian.is_attorney_home === "Yes" && (
        <Box sx={{ mt: 2 }}>
          {showHomeFieldsLabel && homeFieldsLabel && (
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              {homeFieldsLabel}
            </Typography>
          )}
          <AttorneyHomeAddressFields
            {...commonHomeProps}
            googlePlaceholder={googlePlaceholder}
            streetTextFieldProps={
              streetErrorKey
                ? {
                    error: !!errors[streetErrorKey],
                    helperText: errors[streetErrorKey],
                  }
                : {}
            }
            zipTextFieldProps={
              zipErrorKey
                ? {
                    error: !!errors[zipErrorKey],
                    helperText: errors[zipErrorKey],
                  }
                : {}
            }
          />
        </Box>
      )}
    </>
  );
}

AttorneySelectedBlock.propTypes = {
  attorney: PropTypes.object,
  attorneyUi: PropTypes.string.isRequired,
  attorneyCardTitle: PropTypes.string.isRequired,
  officeAddressLabel: PropTypes.string.isRequired,
  guardian: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  guardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  commonHomeProps: PropTypes.object.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
};

function ProbateAttorneyLayout({
  index,
  guardian,
  guardians,
  attorneys,
  loadingAttorneys,
  errors,
  attorneyCardTitle,
  officeAddressLabel,
  commonHomeProps,
  handleAttorneySelection,
  handleAttorneyHomeAddressChange,
}) {
  const attorney = guardian.attorney
    ? attorneys.find((a) => a.value === guardian.attorney)
    : null;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Step 1: Select Attorney
        </Typography>
        <AttorneySearchAutocomplete
          index={index}
          guardian={guardian}
          guardians={guardians}
          attorneys={attorneys}
          loadingAttorneys={loadingAttorneys}
          attorneyUi="probate"
          errors={errors}
          handleAttorneySelection={handleAttorneySelection}
        />
      </Box>
      {guardian.attorney && attorney && (
        <>
          <HistoricAttorneyInfoCard
            title={attorneyCardTitle}
            attorney={attorney}
            officeAddressLabel={officeAddressLabel}
          />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Step 2: Address Information
            </Typography>
            <AttorneyHomeAddressToggle
              guardian={guardian}
              index={index}
              guardians={guardians}
              attorneys={attorneys}
              errors={errors}
              errorKeys={{ isAttorneyHome: `guardians.${index}.is_attorney_home` }}
              handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
            />
          </Box>
          {guardian.is_attorney_home === "Yes" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Enter Home Address:
              </Typography>
              <AttorneyHomeAddressFields
                {...commonHomeProps}
                googlePlaceholder="Search Home Address"
                streetTextFieldProps={{
                  error: !!errors[`guardians.${index}.address`],
                  helperText: errors[`guardians.${index}.address`],
                }}
                zipTextFieldProps={{
                  error: !!errors[`guardians.${index}.zip_code`],
                  helperText: errors[`guardians.${index}.zip_code`],
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

ProbateAttorneyLayout.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  attorneyCardTitle: PropTypes.string.isRequired,
  officeAddressLabel: PropTypes.string.isRequired,
  commonHomeProps: PropTypes.object.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
};

function GuardianshipTrusteeAttorneyLayout({
  attorneyUi,
  index,
  guardian,
  guardians,
  attorneys,
  loadingAttorneys,
  errors,
  attorneyCardTitle,
  officeAddressLabel,
  commonHomeProps,
  handleAttorneySelection,
  handleAttorneyHomeAddressChange,
}) {
  const showBlock = guardian.is_attorney === "Yes" && guardian.attorney;
  const attorney = showBlock
    ? attorneys.find((a) => a.value === guardian.attorney)
    : null;
  const isGuardianship = attorneyUi === "guardianship";

  return (
    <Box sx={{ mt: 2 }}>
      <AttorneySearchAutocomplete
        index={index}
        guardian={guardian}
        guardians={guardians}
        attorneys={attorneys}
        loadingAttorneys={loadingAttorneys}
        attorneyUi={attorneyUi}
        errors={errors}
        handleAttorneySelection={handleAttorneySelection}
      />
      {showBlock && (
        <AttorneySelectedBlock
          attorney={attorney}
          attorneyUi={attorneyUi}
          attorneyCardTitle={attorneyCardTitle}
          officeAddressLabel={officeAddressLabel}
          guardian={guardian}
          index={index}
          guardians={guardians}
          attorneys={attorneys}
          errors={errors}
          options={{
            showHomeToggle: true,
            homeErrorKeys: isGuardianship
              ? { isAttorneyHome: `guardians.${index}.is_attorney_home` }
              : {},
            googlePlaceholder: isGuardianship ? "Search Home Address" : "Search Address",
            streetErrorKey: isGuardianship
              ? `guardians.${index}.guardian_street_address_1`
              : undefined,
            zipErrorKey: isGuardianship
              ? `guardians.${index}.guardian_zip`
              : undefined,
            showHomeFieldsLabel: isGuardianship,
          }}
          commonHomeProps={commonHomeProps}
          handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
        />
      )}
    </Box>
  );
}

GuardianshipTrusteeAttorneyLayout.propTypes = {
  attorneyUi: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  attorneyCardTitle: PropTypes.string.isRequired,
  officeAddressLabel: PropTypes.string.isRequired,
  commonHomeProps: PropTypes.object.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
};

/**
 * Config-driven guardian attorney section (Phase 9.10B).
 * Used by historic creation; retrieval uses RetrievalAttorneySection with its own config.
 */
export default function BondAttorneyInformationBase({
  index,
  guardian,
  guardians,
  cfg,
  keys,
  attorneys,
  loadingAttorneys,
  errors = {},
  data,
  updateData,
  gVal,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  allCities,
  allStates,
  loadingCitiesStates,
  handleAttorneyHomeAddressChange,
  handleAttorneySelection,
  handleGoogleCallback,
  handleZipCodeChange,
}) {
  const attorneyUi = cfg.attorneyUi || "default";
  const attorneyCardTitle = getAttorneyCardTitle(attorneyUi);
  const officeAddressLabel = getAttorneyOfficeAddressLabel(attorneyUi);

  const commonHomeProps = {
    index,
    keys,
    guardian,
    guardians,
    gVal,
    loadingCitiesStates,
    allCities,
    allStates,
    GoogleAutoComplete,
    LabeledCreatableSelect,
    data,
    updateData,
    handleGoogleCallback,
    handleZipCodeChange,
  };

  const layoutProps = {
    index,
    guardian,
    guardians,
    attorneys,
    loadingAttorneys,
    errors,
    attorneyCardTitle,
    officeAddressLabel,
    commonHomeProps,
    handleAttorneySelection,
    handleAttorneyHomeAddressChange,
  };

  if (attorneyUi === "probate") {
    return <ProbateAttorneyLayout {...layoutProps} />;
  }

  if (attorneyUi === "guardianship" || attorneyUi === "trustee") {
    return (
      <GuardianshipTrusteeAttorneyLayout attorneyUi={attorneyUi} {...layoutProps} />
    );
  }

  const showVaBlock = guardian.is_attorney === "Yes" && guardian.attorney;
  const vaAttorney = showVaBlock
    ? attorneys.find((a) => a.value === guardian.attorney)
    : null;

  return (
    <Box sx={{ mt: 2 }}>
      <AttorneySearchAutocomplete
        index={index}
        guardian={guardian}
        guardians={guardians}
        attorneys={attorneys}
        loadingAttorneys={loadingAttorneys}
        attorneyUi={attorneyUi}
        errors={errors}
        handleAttorneySelection={handleAttorneySelection}
      />
      {showVaBlock && (
        <HistoricAttorneyInfoCard
          title={attorneyCardTitle}
          attorney={vaAttorney}
          officeAddressLabel={officeAddressLabel}
        />
      )}
    </Box>
  );
}

BondAttorneyInformationBase.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  cfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  gVal: PropTypes.func.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
};
